import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebaseConfig";
// Legacy stylesheet stays imported: it still carries global rules other pages
// may rely on. All new styling is scoped under .opsui- (see OpsUi.css).
import "./Vendors.css";
import {
  OpsModal,
  ConfirmDialog,
  Pill,
  StatusPill,
  DuePill,
  Field,
  EmptyState,
  StateSelect,
  SecretInput,
  SecretText,
  CopyButton,
  WebsiteLink,
  SortHeader,
  dueStatus,
  formatDate,
} from "./OpsUi";

const EMPTY_VENDOR = {
  companyName: "",
  ein: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  email: "",
  contactName: "",
  industry: "",
  website: "",
  username: "",
  password: "",
  insuranceCertificates: "No",
  generalLiability: "",
  commercialAuto: "",
  workersComp: "",
  addNotes: "",
  status: "Active",
  fileUpload: [],
};

const MANDATORY_FIELDS = [
  ["companyName", "Company Name"],
  ["address", "Address"],
  ["city", "City"],
  ["state", "State"],
  ["zipCode", "Zip Code"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["contactName", "Owner/Contact Name"],
  ["industry", "Industry"],
];

const INSURANCE_FIELDS = [
  ["generalLiability", "General Liability"],
  ["commercialAuto", "Commercial Auto"],
  ["workersComp", "Workers Comp"],
];

// Worst (soonest / most overdue) insurance date on a record.
const insuranceSummary = (record) => {
  if (record.insuranceCertificates !== "Yes") return null;
  const statuses = INSURANCE_FIELDS.map(([key, policy]) => ({
    key,
    policy,
    value: record[key],
    ...dueStatus(record[key]),
  })).filter((s) => s.tone !== "neutral");
  if (!statuses.length) return null;
  return statuses.sort((a, b) => b.rank - a.rank || a.days - b.days)[0];
};

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "companyName", dir: "asc" });

  // form modal (add or edit)
  const [formMode, setFormMode] = useState(null); // "add" | "edit" | null
  const [formData, setFormData] = useState({ ...EMPTY_VENDOR });
  const [formFiles, setFormFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  // details
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // add note
  const [showAddNotePopup, setShowAddNotePopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteFiles, setNoteFiles] = useState([]);
  const [noteSaving, setNoteSaving] = useState(false);

  const storage = getStorage();

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const snap = await getDocs(collection(db, "vendors"));
        setVendors(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
  }, []);

  // ---------------------------------------------------------------- helpers
  const uploadVendorFiles = async (companyName, files) => {
    const urls = [];
    for (const file of Array.from(files || [])) {
      const storageRef = ref(storage, `vendors/${companyName}/${file.name}`);
      await uploadBytes(storageRef, file);
      urls.push(await getDownloadURL(storageRef));
    }
    return urls;
  };

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAdd = () => {
    setFormData({ ...EMPTY_VENDOR });
    setFormFiles([]);
    setFormMode("add");
  };

  const openEdit = () => {
    if (!selectedVendor) return;
    setFormData({ ...EMPTY_VENDOR, ...selectedVendor });
    setFormFiles([]);
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setFormFiles([]);
  };

  // ---------------------------------------------------------------- save
  const handleAddVendor = async () => {
    for (const [field, label] of MANDATORY_FIELDS) {
      if (!String(formData[field] || "").trim()) {
        alert(`Please fill out the ${label} field.`);
        return;
      }
    }

    setSaving(true);
    try {
      const uploaded = await uploadVendorFiles(formData.companyName, formFiles);
      const payload = { ...formData, fileUpload: [...(formData.fileUpload || []), ...uploaded] };
      delete payload.id;
      const docRef = await addDoc(collection(db, "vendors"), payload);
      setVendors((prev) => [...prev, { id: docRef.id, ...payload }]);
      closeForm();
    } catch (error) {
      console.error("Error adding vendor:", error);
      alert("Could not add vendor.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!formData.id) return;
    if (!String(formData.companyName || "").trim()) {
      alert("Please fill out the Company Name field.");
      return;
    }

    setSaving(true);
    try {
      const uploaded = await uploadVendorFiles(formData.companyName, formFiles);
      const payload = {
        ...formData,
        fileUpload: [...(Array.isArray(formData.fileUpload) ? formData.fileUpload : []), ...uploaded],
      };
      const updatePayload = { ...payload };
      delete updatePayload.id;
      await updateDoc(doc(db, "vendors", formData.id), updatePayload);
      setVendors((prev) => prev.map((v) => (v.id === formData.id ? payload : v)));
      setSelectedVendor(payload);
      closeForm();
    } catch (error) {
      console.error("Error saving vendor changes:", error);
      alert("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedVendor) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "vendors", selectedVendor.id));
      setVendors((prev) => prev.filter((v) => v.id !== selectedVendor.id));
      setSelectedVendor(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting vendor:", error);
      alert("Could not delete vendor.");
    } finally {
      setDeleting(false);
    }
  };

  const closeAddNotePopup = () => {
    setShowAddNotePopup(false);
    setNoteText("");
    setNoteFiles([]);
  };

  const handleSaveNote = async () => {
    if (!noteText.trim() && noteFiles.length === 0) {
      alert("Please add a note or upload files.");
      return;
    }
    setNoteSaving(true);
    try {
      const uploadedFiles = [];
      for (const file of noteFiles) {
        const storageRef = ref(storage, `vendors/${selectedVendor.companyName}/notes/${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedFiles.push({ fileName: file.name, fileURL: downloadURL });
      }

      const newNote = {
        text: noteText.trim(),
        timestamp: new Date().toLocaleString(),
        files: uploadedFiles,
      };
      const updatedNotes = Array.isArray(selectedVendor.notes)
        ? [...selectedVendor.notes, newNote]
        : [newNote];

      await updateDoc(doc(db, "vendors", selectedVendor.id), { notes: updatedNotes });
      setVendors((prev) =>
        prev.map((v) => (v.id === selectedVendor.id ? { ...v, notes: updatedNotes } : v))
      );
      setSelectedVendor((prev) => ({ ...prev, notes: updatedNotes }));
      closeAddNotePopup();
    } catch (error) {
      console.error("Error saving note:", error);
      alert("Could not save note.");
    } finally {
      setNoteSaving(false);
    }
  };

  // ---------------------------------------------------------------- list
  const industries = useMemo(
    () =>
      Array.from(new Set(vendors.map((v) => String(v.industry || "").trim()).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [vendors]
  );

  const handleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const filteredSortedVendors = useMemo(() => {
    const q = search.trim().toLowerCase();
    return vendors
      .filter((v) => {
        if (industryFilter && String(v.industry || "").trim() !== industryFilter) return false;
        if (statusFilter === "active" && (v.status || "Active") !== "Active") return false;
        if (statusFilter === "terminated" && (v.status || "Active") === "Active") return false;
        if (!q) return true;
        return [v.companyName, v.industry, v.contactName, v.email, v.phone, v.website, v.city]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const av = String(a[sort.key] || "");
        const bv = String(b[sort.key] || "");
        const cmp = av.localeCompare(bv, undefined, { sensitivity: "base" });
        if (cmp !== 0) return sort.dir === "asc" ? cmp : -cmp;
        return String(a.companyName || "").localeCompare(String(b.companyName || ""), undefined, {
          sensitivity: "base",
        });
      });
  }, [vendors, search, industryFilter, statusFilter, sort]);

  const stats = useMemo(() => {
    const active = vendors.filter((v) => (v.status || "Active") === "Active").length;
    const expiring = vendors.filter((v) => {
      const s = insuranceSummary(v);
      return s && (s.tone === "warn" || s.tone === "danger") && (v.status || "Active") === "Active";
    }).length;
    return { total: vendors.length, active, inactive: vendors.length - active, expiring };
  }, [vendors]);

  // ---------------------------------------------------------------- render: form
  const renderForm = () => {
    const isAdd = formMode === "add";
    const req = (field) => isAdd && MANDATORY_FIELDS.some(([f]) => f === field);
    return (
      <div className="opsui-form-grid">
        <div className="opsui-section-title">Company</div>
        <Field label="Company Name" required={req("companyName")}>
          <input className="opsui-input" name="companyName" value={formData.companyName || ""} onChange={onFormChange} />
        </Field>
        <Field label="Industry" required={req("industry")}>
          <input
            className="opsui-input"
            name="industry"
            list="vend-industry-list"
            value={formData.industry || ""}
            onChange={onFormChange}
          />
          <datalist id="vend-industry-list">
            {industries.map((i) => (
              <option key={i} value={i} />
            ))}
          </datalist>
        </Field>
        <Field label="EIN">
          <input className="opsui-input" name="ein" value={formData.ein || ""} onChange={onFormChange} />
        </Field>
        <Field label="Status">
          <select className="opsui-select" name="status" value={formData.status || "Active"} onChange={onFormChange}>
            <option value="Active">Active</option>
            <option value="Terminated">Terminated</option>
          </select>
        </Field>

        <div className="opsui-section-title">Contact</div>
        <Field label="Owner/Contact Name" required={req("contactName")}>
          <input className="opsui-input" name="contactName" value={formData.contactName || ""} onChange={onFormChange} />
        </Field>
        <Field label="Phone" required={req("phone")}>
          <input className="opsui-input" name="phone" type="tel" value={formData.phone || ""} onChange={onFormChange} />
        </Field>
        <Field label="Email" required={req("email")} wide>
          <input className="opsui-input" name="email" type="email" value={formData.email || ""} onChange={onFormChange} />
        </Field>
        <Field label="Address" required={req("address")} wide>
          <input className="opsui-input" name="address" value={formData.address || ""} onChange={onFormChange} />
        </Field>
        <Field label="City" required={req("city")}>
          <input className="opsui-input" name="city" value={formData.city || ""} onChange={onFormChange} />
        </Field>
        <div className="opsui-form-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="State" required={req("state")}>
            <StateSelect value={formData.state} onChange={onFormChange} />
          </Field>
          <Field label="Zip Code" required={req("zipCode")}>
            <input className="opsui-input" name="zipCode" value={formData.zipCode || ""} onChange={onFormChange} />
          </Field>
        </div>

        <div className="opsui-section-title">Online account</div>
        <Field label="Website" wide hint="Vendor portal or company site, e.g. www.vendor.com">
          <input
            className="opsui-input"
            name="website"
            type="url"
            inputMode="url"
            placeholder="https://"
            value={formData.website || ""}
            onChange={onFormChange}
          />
        </Field>
        <Field label="Username">
          <input
            className="opsui-input"
            name="username"
            autoComplete="off"
            value={formData.username || ""}
            onChange={onFormChange}
          />
        </Field>
        <Field label="Password">
          <SecretInput name="password" value={formData.password} onChange={onFormChange} />
        </Field>

        <div className="opsui-section-title">Insurance</div>
        <Field label="Insurance Certificates">
          <select
            className="opsui-select"
            name="insuranceCertificates"
            value={formData.insuranceCertificates || "No"}
            onChange={onFormChange}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </Field>
        <div />
        {formData.insuranceCertificates === "Yes" &&
          INSURANCE_FIELDS.map(([key, label]) => (
            <Field key={key} label={`${label} Exp. Date`}>
              <input className="opsui-input" type="date" name={key} value={formData[key] || ""} onChange={onFormChange} />
            </Field>
          ))}

        <div className="opsui-section-title">Notes & files</div>
        <Field label="Notes" wide>
          <textarea className="opsui-textarea" name="addNotes" value={formData.addNotes || ""} onChange={onFormChange} />
        </Field>
        <Field
          label={isAdd ? "File Upload" : "Add Files"}
          wide
          hint={formFiles.length ? `${formFiles.length} file(s) will upload when you save.` : "PDF, images or spreadsheets"}
        >
          <input
            className="opsui-file"
            type="file"
            multiple
            accept=".pdf,.jpeg,.jpg,.heic,.hic,.png,.xlsx,.xls,.csv,.doc,.docx"
            onChange={(e) => setFormFiles(Array.from(e.target.files || []))}
          />
        </Field>
      </div>
    );
  };

  // ---------------------------------------------------------------- render: details
  const renderDetails = (v) => {
    const notes = Array.isArray(v.notes) ? v.notes : [];
    const files = Array.isArray(v.fileUpload) ? v.fileUpload : [];
    const address = [v.address, [v.city, v.state].filter(Boolean).join(", "), v.zipCode]
      .filter(Boolean)
      .join(" ");
    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div className="opsui-pills">
          <StatusPill status={v.status || "Active"} />
          {v.industry && <Pill tone="info" plain>{v.industry}</Pill>}
        </div>

        <dl className="opsui-dl">
          <div>
            <dt>Contact</dt>
            <dd>{v.contactName || "—"}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{v.phone ? <a className="opsui-link" href={`tel:${v.phone}`}>{v.phone}</a> : "—"}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{v.email ? <a className="opsui-link" href={`mailto:${v.email}`}>{v.email}</a> : "—"}</dd>
          </div>
          <div>
            <dt>EIN</dt>
            <dd>{v.ein || "—"}</dd>
          </div>
          <div className="is-wide">
            <dt>Address</dt>
            <dd>{address || "—"}</dd>
          </div>
        </dl>

        <div className="opsui-card" style={{ margin: 0 }}>
          <div className="opsui-card-head">
            <h3 className="opsui-card-title">Online account</h3>
          </div>
          <div className="opsui-card-body">
            <dl className="opsui-dl">
              <div className="is-wide">
                <dt>Website</dt>
                <dd>
                  <WebsiteLink value={v.website} />
                </dd>
              </div>
              <div>
                <dt>Username</dt>
                <dd>
                  {v.username ? (
                    <span className="opsui-secret">
                      <span className="opsui-secret-value">{v.username}</span>
                      <CopyButton value={v.username} />
                    </span>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <div>
                <dt>Password</dt>
                <dd>
                  <SecretText value={v.password} />
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div>
          <div className="opsui-label" style={{ marginBottom: 8 }}>
            Insurance certificates: {v.insuranceCertificates || "No"}
          </div>
          {v.insuranceCertificates === "Yes" && (
            <dl className="opsui-dl" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
              {INSURANCE_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <dt>{label}</dt>
                  <dd>
                    <DuePill value={v[key]} />
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <div>
          <div className="opsui-label" style={{ marginBottom: 6 }}>Notes</div>
          <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
            {v.addNotes || <span className="opsui-muted">No notes available.</span>}
          </div>
        </div>

        <div>
          <div className="opsui-label" style={{ marginBottom: 8 }}>Activity log</div>
          {notes.length ? (
            <ul className="opsui-list">
              {notes.map((note, index) => (
                <li key={index} className="opsui-list-item">
                  <div className="opsui-list-item-body">
                    <div className="opsui-list-meta">{note.timestamp}</div>
                    <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{note.text}</div>
                    {Array.isArray(note.files) && note.files.length > 0 && (
                      <div className="opsui-pills" style={{ marginTop: 6 }}>
                        {note.files.map((file, fi) => (
                          <a key={fi} className="opsui-link" href={file.fileURL} target="_blank" rel="noopener noreferrer">
                            📎 {file.fileName}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <span className="opsui-muted" style={{ fontSize: 14 }}>No notes yet.</span>
          )}
        </div>

        <div>
          <div className="opsui-label" style={{ marginBottom: 8 }}>Files</div>
          {files.length ? (
            <div className="opsui-pills">
              {files.map((file, index) => (
                <a key={index} className="opsui-link" href={file} target="_blank" rel="noopener noreferrer">
                  📄 File {index + 1}
                </a>
              ))}
            </div>
          ) : (
            <span className="opsui-muted" style={{ fontSize: 14 }}>No files uploaded.</span>
          )}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------- render
  return (
    <div className="opsui-root opsui-page">
      <div className="opsui-header">
        <div>
          <h1 className="opsui-title">Vendors</h1>
          <p className="opsui-subtitle">Suppliers and service vendors, their contacts, portal logins and insurance.</p>
        </div>
        <div className="opsui-header-actions">
          <button type="button" className="opsui-btn is-primary" onClick={openAdd}>
            + Add vendor
          </button>
        </div>
      </div>

      <div className="opsui-stats">
        <div className="opsui-stat">
          <div className="opsui-stat-label">Total vendors</div>
          <div className="opsui-stat-value">{stats.total}</div>
        </div>
        <div className="opsui-stat is-success">
          <div className="opsui-stat-label">Active</div>
          <div className="opsui-stat-value">{stats.active}</div>
        </div>
        <div className="opsui-stat">
          <div className="opsui-stat-label">Terminated</div>
          <div className="opsui-stat-value">{stats.inactive}</div>
        </div>
        <div className={`opsui-stat ${stats.expiring ? "is-warn" : ""}`}>
          <div className="opsui-stat-label">Insurance expiring / expired</div>
          <div className="opsui-stat-value">{stats.expiring}</div>
        </div>
      </div>

      <div className="opsui-card">
        <div className="opsui-toolbar">
          <input
            className="opsui-input"
            type="search"
            placeholder="Search company, contact, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="opsui-select" value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)}>
            <option value="">All industries</option>
            {industries.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
          <select className="opsui-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="terminated">Terminated</option>
          </select>
          {(search || industryFilter || statusFilter !== "all") && (
            <button
              type="button"
              className="opsui-btn is-ghost is-sm"
              onClick={() => {
                setSearch("");
                setIndustryFilter("");
                setStatusFilter("all");
              }}
            >
              Clear
            </button>
          )}
          <span className="opsui-toolbar-spacer" />
          <span className="opsui-count">
            {filteredSortedVendors.length} of {vendors.length}
          </span>
        </div>

        {loading ? (
          <EmptyState title="Loading vendors…" />
        ) : filteredSortedVendors.length === 0 ? (
          vendors.length === 0 ? (
            <EmptyState
              title="No vendors yet"
              action={
                <button type="button" className="opsui-btn is-primary" onClick={openAdd}>
                  + Add your first vendor
                </button>
              }
            >
              Add suppliers you buy chemicals, equipment and materials from.
            </EmptyState>
          ) : (
            <EmptyState title="No vendors match your filters" />
          )
        ) : (
          <div className="opsui-table-wrap">
            <table className="opsui-table">
              <thead>
                <tr>
                  <SortHeader label="Company" sortKey="companyName" sort={sort} onSort={handleSort} />
                  <SortHeader label="Industry" sortKey="industry" sort={sort} onSort={handleSort} />
                  <th className="opsui-hide-sm">Contact</th>
                  <th className="opsui-hide-sm">Website</th>
                  <th className="opsui-hide-sm">Insurance</th>
                  <SortHeader label="Status" sortKey="status" sort={sort} onSort={handleSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredSortedVendors.map((vendor) => {
                  const ins = insuranceSummary(vendor);
                  const inactive = (vendor.status || "Active") !== "Active";
                  return (
                    <tr
                      key={vendor.id}
                      className={`is-clickable${inactive ? " is-muted" : ""}`}
                      onClick={() => setSelectedVendor(vendor)}
                    >
                      <td>
                        <div className="opsui-cell-main">{vendor.companyName || "Unnamed vendor"}</div>
                        {(vendor.city || vendor.state) && (
                          <div className="opsui-cell-sub">{[vendor.city, vendor.state].filter(Boolean).join(", ")}</div>
                        )}
                      </td>
                      <td>{vendor.industry || <span className="opsui-muted">—</span>}</td>
                      <td className="opsui-hide-sm">
                        <div>{vendor.contactName || "—"}</div>
                        <div className="opsui-cell-sub">{vendor.phone || vendor.email || ""}</div>
                      </td>
                      <td className="opsui-hide-sm" onClick={(e) => e.stopPropagation()}>
                        <WebsiteLink value={vendor.website} />
                      </td>
                      <td className="opsui-hide-sm">
                        {ins ? (
                          <Pill tone={ins.tone} title={`${ins.policy} expires ${formatDate(ins.value)}`}>
                            {ins.label}
                          </Pill>
                        ) : (
                          <span className="opsui-muted">{vendor.insuranceCertificates === "Yes" ? "No dates" : "—"}</span>
                        )}
                      </td>
                      <td>
                        <StatusPill status={vendor.status || "Active"} />
                      </td>
                      <td className="opsui-cell-actions">
                        <button
                          type="button"
                          className="opsui-btn is-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVendor(vendor);
                          }}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- details ---------- */}
      {selectedVendor && !formMode && (
        <OpsModal
          title={selectedVendor.companyName || "Vendor"}
          subtitle="Vendor details"
          onClose={() => setSelectedVendor(null)}
          footer={
            <>
              <div className="opsui-foot-left">
                <button type="button" className="opsui-btn is-danger" onClick={() => setShowDeleteConfirmation(true)}>
                  Delete
                </button>
              </div>
              <button type="button" className="opsui-btn" onClick={() => setShowAddNotePopup(true)}>
                Add note
              </button>
              <button type="button" className="opsui-btn" onClick={openEdit}>
                Edit
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={() => setSelectedVendor(null)}>
                Close
              </button>
            </>
          }
        >
          {renderDetails(selectedVendor)}
        </OpsModal>
      )}

      {/* ---------- add / edit ---------- */}
      {formMode && (
        <OpsModal
          title={formMode === "add" ? "Add vendor" : `Edit ${formData.companyName || "vendor"}`}
          onClose={closeForm}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="opsui-btn is-primary"
                onClick={formMode === "add" ? handleAddVendor : handleSaveChanges}
                disabled={saving}
              >
                {saving ? "Saving…" : formMode === "add" ? "Save vendor" : "Save changes"}
              </button>
            </>
          }
        >
          {renderForm()}
        </OpsModal>
      )}

      {/* ---------- add note ---------- */}
      {showAddNotePopup && selectedVendor && (
        <OpsModal
          title="Add note"
          subtitle={selectedVendor.companyName}
          onClose={closeAddNotePopup}
          width={520}
          stacked
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={closeAddNotePopup} disabled={noteSaving}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={handleSaveNote} disabled={noteSaving}>
                {noteSaving ? "Saving…" : "Save note"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 12 }}>
            <Field label="Note">
              <textarea
                className="opsui-textarea"
                rows="4"
                placeholder="Enter your note here…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </Field>
            <Field label="Attachments">
              <input
                className="opsui-file"
                type="file"
                accept="image/*, .pdf"
                multiple
                onChange={(e) => setNoteFiles(Array.from(e.target.files || []))}
              />
            </Field>
          </div>
        </OpsModal>
      )}

      {showDeleteConfirmation && selectedVendor && (
        <ConfirmDialog
          title="Delete vendor?"
          message={
            <>
              <strong>{selectedVendor.companyName}</strong> will be permanently removed. This cannot be undone.
            </>
          }
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          busy={deleting}
        />
      )}
    </div>
  );
};

export default Vendors;
